import { Component, Show, createSignal, onMount } from 'solid-js';

interface InputAreaProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (text: string) => void;
  onStop?: () => void;
  disabled: boolean;
  webSearchEnabled: boolean;
  webSearchConfigured: boolean;
  onToggleWebSearch: () => void;
  isStreaming?: boolean;
  queueCount?: number;
}

export const InputArea: Component<InputAreaProps> = (props) => {
  let textareaRef: HTMLTextAreaElement | undefined;
  const [rows, setRows] = createSignal(1);
  const templates: { label: string; text: string }[] = [
    { label: '🤝 Friendly reply', text: 'Please respond in a friendly, concise tone.' },
    { label: '🔍 Ask for sources', text: 'Provide citations and links to reputable sources.' },
    { label: '🧪 Test prompt', text: 'Summarize the key points in bullet form, then propose next steps.' },
    { label: '🧩 Chain-of-thought style', text: 'Think step by step. Present only the final answer clearly.' },
  ];
  const [selectedTemplate, setSelectedTemplate] = createSignal<string>('');

  onMount(() => {
    if (textareaRef) {
      // Don't auto-focus on mobile to prevent unwanted keyboard opening
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (!isMobile) {
        textareaRef.focus();
      }

      // Handle virtual keyboard on mobile
      if (isMobile) {
        const handleFocus = () => {
          // Scroll the input into view when virtual keyboard opens
          setTimeout(() => {
            if (textareaRef) {
              textareaRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 300); // Delay to wait for keyboard animation
        };

        const handleBlur = () => {
          // Reset any layout adjustments when keyboard closes
          setTimeout(() => {
            window.scrollTo(0, 0);
          }, 100);
        };

        textareaRef.addEventListener('focus', handleFocus);
        textareaRef.addEventListener('blur', handleBlur);

        return () => {
          if (textareaRef) {
            textareaRef.removeEventListener('focus', handleFocus);
            textareaRef.removeEventListener('blur', handleBlur);
          }
        };
      }
    }
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Allow new line on Shift+Enter
        return;
      } else if (e.ctrlKey || e.metaKey) {
        // Send on Ctrl+Enter or Cmd+Enter
        e.preventDefault();
        handleSend();
      } else {
        // Send on Enter (default behavior)
        e.preventDefault();
        handleSend();
      }
    } else if (e.key === 'Escape') {
      // Clear input on Escape
      e.preventDefault();
      props.onChange('');
      if (textareaRef) {
        textareaRef.blur();
      }
    }
  };

  const handleInput = (e: Event) => {
    const target = e.currentTarget as HTMLTextAreaElement;
    props.onChange(target.value);
    
    // Auto-resize textarea
    target.style.height = 'auto';
    const lineHeight = 24;
    const newRows = Math.min(Math.max(1, Math.ceil(target.scrollHeight / lineHeight)), 4);
    setRows(newRows);
  };
  
  const handleSend = () => {
    if (props.value.trim() && !props.disabled && !props.isStreaming) {
      props.onSend(props.value);
      props.onChange('');
      setRows(1);
      
      // Refocus input after sending
      if (textareaRef) {
        textareaRef.focus();
      }
    }
  };

  const getPlaceholder = () => {
    if (props.disabled) {
      return 'Please load a model to start chatting...';
    } else if (props.isStreaming) {
      return 'AI is responding...';
    } else {
      return 'Type your message... (Enter to send, Shift+Enter for new line)';
    }
  };
  
  return (
    <section class="input-area" role="region" aria-label="Message input">
      <div class="input-controls">
        <details class="pill-menu">
          <summary class="pill" aria-label="Open input options">Options</summary>
          <div class="pill-body">
        <Show when={props.webSearchConfigured}>
          <label class="web-search-toggle" for="web-search-checkbox">
            <input
              id="web-search-checkbox"
              type="checkbox"
              checked={props.webSearchEnabled}
              onChange={() => props.onToggleWebSearch()}
              disabled={props.disabled}
              aria-describedby="web-search-help"
            />
            <span class="checkbox-icon" role="img" aria-hidden="true">🔍</span>
            <span class="checkbox-label">Web Search</span>
          </label>
          <div id="web-search-help" class="visually-hidden">
            Enable web search to include current information in AI responses
          </div>
        </Show>
        
        <Show when={(props.queueCount || 0) > 0}>
          <div class="queue-indicator">
            <span class="queue-icon" role="img" aria-hidden="true">📋</span>
            <span class="queue-text">{props.queueCount} in queue</span>
          </div>
        </Show>
          </div>
        </details>
      </div>

      <div class="input-wrapper">
        <label for="message-input" class="visually-hidden">
          Message input
        </label>
  <div class="input-template-row">
          <select
            aria-label="Prompt templates"
            value={selectedTemplate()}
            onChange={(e) => setSelectedTemplate(e.currentTarget.value)}
          >
            <option value="">Templates…</option>
            {templates.map(t => (
              <option value={t.text}>{t.label}</option>
            ))}
          </select>
          <button
            type="button"
            class="secondary"
            disabled={!selectedTemplate()}
            onClick={() => {
              const t = selectedTemplate();
              if (!t) return;
              const existing = props.value;
              const sep = existing && !existing.endsWith(' ') ? ' ' : '';
              props.onChange(`${existing}${sep}${t}`);
              setSelectedTemplate('');
              textareaRef?.focus();
            }}
            title="Insert template"
          >
            Insert
          </button>
        </div>
        <textarea
          id="message-input"
          ref={textareaRef}
          value={props.value}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholder()}
          disabled={props.disabled || props.isStreaming}
          rows={rows()}
          maxLength={4000}
          aria-describedby="input-help character-count"
          aria-label="Type your message to the AI"
          class="message-textarea"
          spellcheck="true"
          autocomplete="off"
          autocorrect="on"
          
        />
        
        <div class="input-actions">
          <div 
            id="character-count" 
            class="character-counter" 
            aria-live="polite"
            data-over-limit={props.value.length > 4000}
            data-near-limit={props.value.length > 3500}
          >
            {props.value.length}/4000
            {props.value.length > 4000 && (
              <span class="over-limit-warning" role="alert">
                Exceeds limit by {props.value.length - 4000}
              </span>
            )}
          </div>
          
          <Show when={props.isStreaming && props.onStop} fallback={
            <button
              type="button"
              onClick={handleSend}
              disabled={props.disabled || !props.value.trim()}
              aria-describedby="send-help"
              class="send-button"
              aria-keyshortcuts="Enter"
            >
              <span class="button-content">
                <span role="img" aria-hidden="true">📤</span>
                Send
              </span>
            </button>
          }>
            <button
              type="button"
              onClick={props.onStop}
              class="stop-button"
              aria-label="Stop generation"
              title="Stop the AI response"
            >
              <span class="button-content">
                <span role="img" aria-hidden="true">⏹️</span>
                Stop
              </span>
            </button>
          </Show>
        </div>
      </div>

      <div id="input-help" class="input-help visually-hidden">
        Press Enter to send your message, or Shift+Enter to add a new line. 
        Press Escape to clear the input.
      </div>
      
      <div id="send-help" class="visually-hidden">
        Send your message to the AI. The message will be processed along with conversation context.
      </div>

      {/* Keyboard shortcuts help */}
      <div class="keyboard-shortcuts visually-hidden" role="region" aria-label="Keyboard shortcuts">
        <h3>Keyboard shortcuts:</h3>
        <ul>
          <li>Enter: Send message</li>
          <li>Shift+Enter: New line</li>
          <li>Ctrl+Enter: Send message (alternative)</li>
          <li>Escape: Clear input</li>
        </ul>
      </div>
    </section>
  );
};